// @flow

import _ from 'lodash';

import ApiObject from '../ApiObject';

// -- constants
import BookingConstants from '../../constants/BookingConstants';

// -- utils
import DateUtils from '../../utils/DateUtils';

// -- entities
import BookingSettings from './BookingSettings';
import BookingDayRoomSettings from './BookingDayRoomSettings';
import BookingSettingsRoom from './BookingSettingsRoom';
import BookingSlotCapacitySettings from './BookingSlotCapacitySettings';

/**
 * Define the settings for the booking on a day.
 * It defines the capacities and the opening and closing hour.
 */
export default class BookingDaySettings extends ApiObject {
  /**
   * Define the state for the day (Can be open or closed).
   * See BookingConstants.State
   */
  state: number = BookingConstants.State.CLOSED;

  /**
   * The opening hour for the day on the part.
   * Time in ms.
   */
  openingHour: number = 0;

  /**
   * The closing hour for the day on the part.
   * Time in ms.
   */
  closingHour: number = 0;

  /**
   *
   */
  averageDurationOfMeal = DateUtils.HOUR_AS_MILLISECONDS;

  /**
   * The key is the id of the room. It contains the settings for the room for the day.
   */
  roomsSettings: Array<BookingDayRoomSettings> = [];

  postJsonAssignation(data: Object) {
    this.roomsSettings = [];
    _.each(data.roomsSettings, (roomSettingData:Object) => {
      const roomSetting:BookingDayRoomSettings = new BookingDayRoomSettings();
      roomSetting.fromJson(roomSettingData);
      this.roomsSettings.push(roomSetting);
    });
  }

  // validation

  isValid(bookingSettings: BookingSettings): boolean {
    if (!(this.openingHour < this.closingHour
      && DateUtils.isHour(this.openingHour)
      && DateUtils.isHour(this.closingHour))) {
      return false;
    }

    let res = true;
    _.each(this.roomsSettings, (roomSettings: BookingDayRoomSettings) => {
      if (!roomSettings.isValid(bookingSettings, this.openingHour, this.closingHour)) {
        res = false;
        return false; // quit loop
      }
    });

    return res;
  }

  isOpen(): boolean {
    return this.state === BookingConstants.State.OPEN;
  }

  /**
   *
   * @param day The day where we are to set it to the rule.
   * @returns {Array}
   */
  getSpecificsSettingsForRooms(day: number) {
    const list = [];

    _.each(this.roomsSettings, (roomSetting: BookingDayRoomSettings) => {
      _.each(roomSetting.rules, (rule: BookingSlotCapacitySettings) => {

        const ruleCopy = rule.copy();
        ruleCopy.roomId = roomSetting.roomId;
        ruleCopy.day = day;

        list.push(ruleCopy);
      });
    });

    list.sort((a: BookingSlotCapacitySettings, b: BookingSlotCapacitySettings) => {
      return a.date - b.date;
    });

    return list;
  }

  getRoomSettings(room: BookingSettingsRoom): BookingDayRoomSettings {
    return this.getRoomSettingsWithRoomId(room.id);
  }

  getRoomSettingsWithRoomId(roomId: string): BookingDayRoomSettings {
    let dayRoomSettingsSettings = _.find(this.roomsSettings, (roomSettings: BookingDayRoomSettings) => {
      return roomSettings.roomId === roomId;
    });

    if (_.isUndefined(dayRoomSettingsSettings) || _.isNull(dayRoomSettingsSettings)) {
      dayRoomSettingsSettings = new BookingDayRoomSettings();
      dayRoomSettingsSettings.roomId = roomId;
    }

    return dayRoomSettingsSettings;
  }

  setRoomSettings(dayRoomSettingsToSet: BookingDayRoomSettings) {
    const tmp = [];
    let pushed = false;

    _.each(this.roomsSettings, (dayRoomSettings: BookingDayRoomSettings) => {
      if (dayRoomSettings.roomId === dayRoomSettingsToSet.roomId) {
        tmp.push(dayRoomSettingsToSet);
        pushed = true;
      } else {
        tmp.push(dayRoomSettings);
      }
    });

    if (!pushed) {
      tmp.push(dayRoomSettingsToSet);
    }

    this.roomsSettings = tmp;
  }

  addBookingSlotCapacitySettings(bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    const room:BookingDayRoomSettings = this.getRoomSettingsWithRoomId(bookingSlotCapacitySetting.roomId);
    room.addBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setRoomSettings(room);
  }

  updateBookingSlotCapacitySettings(bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    const room:BookingDayRoomSettings = this.getRoomSettingsWithRoomId(bookingSlotCapacitySetting.roomId);
    room.updateBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setRoomSettings(room);
  }

  deleteBookingSlotCapacitySettings(bookingSlotCapacitySetting: BookingSlotCapacitySettings) {
    const room:BookingDayRoomSettings = this.getRoomSettingsWithRoomId(bookingSlotCapacitySetting.roomId);
    room.deleteBookingSlotCapacitySettings(bookingSlotCapacitySetting);
    this.setRoomSettings(room);
  }

  /**
   * Returns the capacity of all the rooms combined.
   * @param day
   */
  getTotalCapacity(): number {
    let total = 0;
    _.each(this.roomsSettings, (roomSetting: BookingDayRoomSettings) => {
      total += roomSetting.capacity;
    });

    return total;
  }
}
